import { BaseEffect } from '../BaseEffect.js';

/**
 * Atmospheric fog effect implementation
 * Creates a dynamic fog effect with depth perception and noise
 */
export class FogEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            fogColorR: 0.8,           // Fog color red component
            fogColorG: 0.9,           // Fog color green component
            fogColorB: 1.0,           // Fog color blue component
            fogDensity: 0.5,          // Overall fog density
            fogDepth: 1.0,            // How much depth affects fog
            noiseScale: 50.0,         // Scale of the noise pattern
            noiseStrength: 0.2,       // How much noise affects the fog
            speed: 1.0,               // Speed of fog movement
            animate: true             // Whether to animate the fog
        };

        super('fog', shaderManager, defaultParams);

        // Set up WebGL blending
        const gl = this.shaderManager.gl;
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform vec3 u_fogColor;
            uniform float u_fogDensity;
            uniform float u_fogDepth;
            uniform float u_noiseScale;
            uniform float u_noiseStrength;
            uniform float u_speed;
            
            varying vec2 v_texcoord;

            // Improved pseudo-random number generator
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // 2D rotation matrix
            mat2 rot2d(float angle) {
                float s = sin(angle);
                float c = cos(angle);
                return mat2(c, -s, s, c);
            }

            // 2D noise with rotation
            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);

                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));

                vec2 u = f * f * (3.0 - 2.0 * f);

                return mix(a, b, u.x) +
                        (c - a)* u.y * (1.0 - u.x) +
                        (d - b) * u.x * u.y;
            }

            // Curl noise for gas dynamics
            vec2 curl(vec2 p, float t) {
                float eps = 0.01;
                float n1 = noise(p + vec2(eps, 0.0));
                float n2 = noise(p + vec2(0.0, eps));
                float n3 = noise(p - vec2(eps, 0.0));
                float n4 = noise(p - vec2(0.0, eps));

                float x = n1 - n3;
                float y = n2 - n4;

                return vec2(y, -x) * 0.5;
            }

            // Enhanced FBM with rotation and curl distortion
            float fbm(vec2 st, float t) {
                float value = 0.0;
                float amplitude = 0.5;
                float frequency = 1.0;
                mat2 rotation = rot2d(t * 0.1);
                
                // Distort the position with curl noise
                vec2 curl_offset = curl(st * 0.5, t) * 0.3;
                st += curl_offset;

                // Add 6 octaves of noise with rotation and movement
                for (int i = 0; i < 6; i++) {
                    // Rotate each octave differently
                    st = rotation * st;
                    
                    // Add time-based movement that varies by octave
                    vec2 offset = vec2(
                        t * 0.1 * (1.0 - amplitude),
                        t * 0.15 * amplitude
                    );
                    
                    // Add noise with movement
                    value += amplitude * noise(st * frequency + offset);
                    
                    // Adjust for next octave
                    frequency *= 2.0;
                    amplitude *= 0.5;
                    
                    // Rotate for next iteration
                    rotation = rot2d(amplitude * 2.0);
                }
                
                return value;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                float t = u_time * u_speed;  // Scale time by speed
                
                // Calculate base depth with non-linear falloff
                float depth = pow(1.0 - uv.y, u_fogDepth);
                depth *= (1.0 + sin(uv.x * 6.28) * 0.1); // Add subtle horizontal variation
                
                // Generate primary noise pattern
                vec2 noisePos = uv * u_noiseScale;
                noisePos.x *= aspect;
                
                // Add multiple layers of animated noise
                float noiseValue = 0.0;
                
                // Layer 1: Base fog structure with speed-adjusted time
                noiseValue += fbm(noisePos, t) * 0.5;
                
                // Layer 2: Faster, smaller detail with speed-adjusted time
                noiseValue += fbm(noisePos * 2.0 + vec2(t * 0.2), t * 1.5) * 0.25;
                
                // Layer 3: Slow, large movements with speed-adjusted time
                noiseValue += fbm(noisePos * 0.5 - vec2(t * 0.05), t * 0.7) * 0.25;
                
                // Normalize and adjust contrast
                noiseValue = (noiseValue * 0.8 + 0.2);
                noiseValue = pow(noiseValue, 1.2);
                
                // Calculate fog amount with depth and noise influence
                float fogAmount = depth * u_fogDensity;
                fogAmount *= noiseValue;
                fogAmount += (noiseValue - 0.5) * u_noiseStrength;
                
                // Add vertical wisping effect
                float wisp = sin(uv.y * 20.0 + noiseValue * 10.0 + t) * 0.5 + 0.5;
                wisp *= (1.0 - uv.y) * 0.3;
                fogAmount += wisp * u_noiseStrength;
                
                // Ensure fog amount stays in valid range
                fogAmount = clamp(fogAmount, 0.0, 1.0);
                
                // Sample original color and mix with fog
                vec4 color = texture2D(u_image, uv);
                color.rgb = mix(color.rgb, u_fogColor, fogAmount);
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_fogColor', { type: '3fv' });
        this.addUniform('u_fogDensity', { type: '1f' });
        this.addUniform('u_fogDepth', { type: '1f' });
        this.addUniform('u_noiseScale', { type: '1f' });
        this.addUniform('u_noiseStrength', { type: '1f' });
        this.addUniform('u_speed', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_fogColor: { 
                value: [this.params.fogColorR, this.params.fogColorG, this.params.fogColorB], 
                type: '3fv' 
            },
            u_fogDensity: { value: this.params.fogDensity, type: '1f' },
            u_fogDepth: { value: this.params.fogDepth, type: '1f' },
            u_noiseScale: { value: this.params.noiseScale, type: '1f' },
            u_noiseStrength: { value: this.params.noiseStrength, type: '1f' },
            u_speed: { value: this.params.speed, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            fogColorR: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Fog Color R'
            },
            fogColorG: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Fog Color G'
            },
            fogColorB: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Fog Color B'
            },
            fogDensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Fog Density'
            },
            fogDepth: {
                min: 0,
                max: 3,
                step: 0.1,
                label: 'Fog Depth'
            },
            noiseScale: {
                min: 1,
                max: 100,
                step: 1,
                label: 'Noise Scale'
            },
            noiseStrength: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Noise Strength'
            },
            speed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 